import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useStateSelector } from '../../context/StateSelectors';
import { getFilterField } from '../../utils/formatName';
import { FilterList } from './components/FilterList';
import FilterComponents from './components/filterComponents';
import { useFilterOptions } from './hooks/useFilterOptions';
import { useBasicFilters } from './hooks/useBasicFilters';

const FilterContainer = ({
    Head,
    handleFilterItems,
    setFilterCount,
    initFilters,
}) => {
    const {
        filters,
        addFilter,
        updateFilter,
        updateWithOpChange,
        removeFilter,
        replaceFilters,
    } = useBasicFilters(initFilters);

    const [selectedFilter, setSelectedFilter] = useState(null);
    const controllersData = useStateSelector((state) => state.controllersData);
    const server = useStateSelector((state) => state.server);

    const contextFields = useMemo(
        () => Head.filter((field) => field.filterCell === 'Context'),
        [Head]
    );
    const itemsFields = useMemo(
        () => Head.filter((field) => field.filterCell === 'Items'),
        [Head]
    );

    const handleFilterItemsCallback = useCallback(handleFilterItems, [
        handleFilterItems,
    ]);

    useEffect(() => {
        if (JSON.stringify(filters) !== JSON.stringify(initFilters)) {
            replaceFilters(initFilters);
        }
    }, [initFilters, replaceFilters]);


    const filterOptions = useFilterOptions(
        contextFields,
        itemsFields,
        controllersData,
        server
    );

    useEffect(() => {
        handleFilterItemsCallback(filters);
        const filtersCount = filters.filter((f) => f.op !== '<').length;
        setFilterCount(filtersCount);
    }, [filters]);

    const handleValueChange = (id) => {
        if (!selectedFilter) return;
        const transformedField = selectedFilter.filterField;

        const existingFilter = filters.find(
            (f) => f.field === transformedField
        );

        if (existingFilter) {
            const updatedValues = existingFilter.value.includes(id)
                ? existingFilter.value.filter((v) => v !== id)
                : [...existingFilter.value, id];

            return updatedValues.length === 0
                ? removeFilter(transformedField, '@')
                : updateFilter(transformedField, '@', updatedValues);
        }
        return addFilter(transformedField, '@', [id]);
    };

    const handleChangeRange = (rangeFilters) => {
        const transformedField = selectedFilter.filterField;

        const cleanedFilters = filters.filter(
            (f) =>
                f.field !== transformedField ||
                (f.op !== '>' && f.op !== '<')
        );

        replaceFilters([
            ...cleanedFilters,
            ...rangeFilters.map((filter) => ({
                ...filter,
                field: transformedField,
            })),
        ]);
    };

    const handleChangeBoolean = (value) => {
        const transformedField = selectedFilter.filterField;
        const existingFilter = filters.find(
            (f) => f.field === transformedField
        );

        if (existingFilter) {
            return value === null
                ? removeFilter(transformedField, '=')
                : updateFilter(transformedField, '=', value);
        }
        return value === null
            ? undefined
            : addFilter(transformedField, '=', value);
    };

    const handleChangeExistence = (value, op) => {
        const transformedField = selectedFilter.filterField;
        const existingFilter = filters.find(
            (f) => f.field === transformedField
        );

        if (existingFilter) {
            if (existingFilter.op === op) {
                return removeFilter(transformedField, op);
            }
            return updateWithOpChange(transformedField, op, null);
        }

        return addFilter(transformedField, op, null);
    };

    const handleChangeText = (value) => {
        const transformedField = selectedFilter.filterField;
        const existingFilter = filters.find(
            (f) => f.field === transformedField
        );

        if (!value || value.trim() === '') {
            // If value is empty, remove filter
            return existingFilter ? removeFilter(transformedField, 'ilike') : undefined;
        }

        if (existingFilter) {
            return updateFilter(transformedField, 'ilike', value);
        }
        return addFilter(transformedField, 'ilike', value);
    };

    const filterHandlers = {
        default: handleValueChange,
        Date: handleChangeRange,
        Range: handleChangeRange,
        Boolean: handleChangeBoolean,
        Existence: handleChangeExistence,
        Text: handleChangeText,
        Select: handleValueChange,
    };

    const FilterComponent = selectedFilter
        ? FilterComponents[selectedFilter.filterCell]
        : null;

    const memoizedFilters = useMemo(() => filters, [filters]);

    return (
        <Box sx={{ backgroundColor: '#fff' }}>
            {selectedFilter ? (
                <FilterComponent
                    filterOptions={
                        filterOptions[selectedFilter.controller] || []
                    }
                    selectedFilter={selectedFilter}
                    filters={memoizedFilters}
                    field={Head.find((f) => f.id === selectedFilter.field)}
                    onValueChange={(id, option) => {
                        const handler =
                            filterHandlers[selectedFilter.filterCell] ||
                            filterHandlers.default;
                        handler(id, option);
                    }}
                    onBack={() => setSelectedFilter(null)}
                />
            ) : (
                <FilterList
                    filters={Head.filter((field) => field.filterCell).map(
                        (field) => ({
                            field: field.id,
                            filterField: getFilterField(field),
                            filterKey: field.filterKey || 'id',
                            label: field.label || field.id,
                            controller: field.filterBy,
                            filterCell: field.filterCell,
                            object: field.object || null,
                            requiredFields: field.requiredFields || [],
                        })
                    )}
                    onFilterSelect={(filter) => setSelectedFilter(filter)}
                    selectedFiltersField={
                        filters.length ? filters.map((f) => f.field) : []
                    }
                />
            )}
        </Box>
    );
};

export default FilterContainer;
